import React, { useEffect, useMemo, useState } from "react";
import DashboardTiles from "./DashboardTiles";
import Topics from "./Topics";
import pluralize from "pluralize";
import DashboardFilters from "./DashboardFilters"; // NEW
import SearchWidget from "./SearchWidget";
import {
  selectLocation,
  selectResetTopic,
  selectSeeAllTopics,
  selectSort,
  selectSurvey,
} from "../Util/Analytics/TopicAnalytics";
import { GetQuestionAvailability } from "../Util/Api";
import * as ENV from "../Util/env";

const DashboardView = ({
  onSubTopicChange,
  onTopicChange,
  onVizTypeChange,
  questionId,
  questions,
  subTopicId,
  topicId,
  topics,
  // location related (optional on dashboard)
  location,
  locations,
  displayLocations,
  displayLocationDropdown,
  onLocationChange,
  setLocationSelectedFromDropdownMenu,
}) => {
  // searchTerm: value used for filtering questions (debounced)
  const [searchTerm, setSearchTerm] = useState("");
  // searchInput: immediate value shown in the SearchWidget input
  const [searchInput, setSearchInput] = useState("");
  // questionType: selected audience filter (Principal | Teacher | Both | null)
  const [questionType, setQuestionType] = useState("Both");
  // questionSortType: selected sort order (Random | By Topic)
  const [questionSortType, setQuestionSortType] = useState("Random");
  // Simple availability set for current location: Set of questionIds
  const [availableQuestionIds, setAvailableQuestionIds] = useState(null); // null => ALL or not computed; Set when computed
  const [locationFilterLoading, setLocationFilterLoading] = useState(false);

  // Stable, session-scoped seed so "Random" order doesn't reshuffle on incidental re-renders
  const seedKey = `shpe:randseed:v1:${ENV.APIBaseURL}`;
  const [randSeed] = useState(() => {
    try {
      const existing = sessionStorage.getItem(seedKey);
      if (existing && !isNaN(parseInt(existing, 10))) return parseInt(existing, 10);
      const s = Math.floor(Math.random() * 0x7fffffff);
      sessionStorage.setItem(seedKey, String(s));
      return s;
    } catch (e) {
      // Fallback when sessionStorage is unavailable
      return Math.floor(Math.random() * 0x7fffffff);
    }
  });

  // Deterministic pseudo-random number generator based on a 32-bit seed
  function prng32(a) {
    return function () {
      let t = (a += 0x6D2B79F5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  // Cheap string hash -> 32-bit unsigned int
  function hash32(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < (str ? str.length : 0); i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }
  // Map a QuestionId to a stable pseudo-random weight using the session seed
  function weightForQuestionId(id) {
    const idHash = hash32(String(id || ""));
    const seed = (randSeed ^ idHash) >>> 0;
    return prng32(seed)();
  }

  // sessionStorage helpers for availability persistence per environment + location
  function makeAvailabilityKey(locationAbbr) {
    return `shpe:avail:v1:${ENV.APIBaseURL}:${locationAbbr}`;
  }
  function loadAvailabilityFromSession(locationAbbr, currentQuestions) {
    try {
      const key = makeAvailabilityKey(locationAbbr);
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const ids = Array.isArray(parsed?.ids) ? parsed.ids : null;
      if (!ids) return null;
      const allowed = new Set((currentQuestions || []).map((q) => q && q.QuestionId).filter(Boolean));
      const pruned = ids.filter((id) => allowed.has(id));
      return new Set(pruned);
    } catch (e) {
      return null;
    }
  }
  function saveAvailabilityToSession(locationAbbr, idsSet, currentQuestions) {
    try {
      const key = makeAvailabilityKey(locationAbbr);
      const allowed = new Set((currentQuestions || []).map((q) => q && q.QuestionId).filter(Boolean));
      const ids = Array.from(idsSet).filter((id) => allowed.has(id));
      const payload = JSON.stringify({ ids });
      sessionStorage.setItem(key, payload);
    } catch (e) {
      // ignore quota or parse errors
    }
  }

  // Track total questions (base set) for logging; memoized to avoid extra dependencies in effects
  const totalQuestions = useMemo(
    () => (Array.isArray(questions) ? questions.length : 0),
    [questions]
  );

  // When location changes away from ALL, fetch available questions for that location
  const locationAbbr = location && location.LocationAbbr;
  useEffect(() => {
    const abbr = location && location.LocationAbbr;
    if (!abbr || abbr === "ALL") {
      setAvailableQuestionIds(null);
      setLocationFilterLoading(false);
      return;
    }

    // Try session cache first for instant filtering
    const cached = loadAvailabilityFromSession(abbr, questions);
    if (cached) {
      setAvailableQuestionIds(cached);
      setLocationFilterLoading(false);
      return;
    }

    setLocationFilterLoading(true);
    
    // Single API call to get all available question IDs for this location
    GetQuestionAvailability(abbr)
      .then((resp) => {
        const questionIds = (resp && resp.data) || [];
        const ids = new Set(questionIds);
        setAvailableQuestionIds(ids);
        saveAvailabilityToSession(abbr, ids, questions);
      })
      .catch((err) => {
        console.error('Failed to fetch question availability:', err);
        setAvailableQuestionIds(new Set());
      })
      .finally(() => {
        setLocationFilterLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationAbbr, questions]);

  useEffect(() => {
    document.title = "School Health Profiles Explorer – Dashboard";
  }, []);

  // Debounced search callback from SearchWidget
  function handleDebouncedSearch(nextSearchTerm) {
    setSearchTerm(nextSearchTerm);
    // TODO: ADOBE-ANALYTICS: search term (fire here once per debounced value)
  }

  // Reset search & topic selection (triggered by SearchWidget reset)
  function handleSearchReset() {
    selectResetTopic();
    setSearchInput("");
    setSearchTerm("");
    onTopicChange(null);
    // Also reset location filter back to All Locations on homepage
    try {
      const allLocation =
        (locations && locations.find((x) => x && x.LocationAbbr === "ALL")) ||
        { LocationAbbr: "ALL", LocationDesc: "All Locations", LocationType: "ALL" };
      if (!location || location.LocationAbbr !== "ALL") {
        onLocationChange && onLocationChange(allLocation, null);
      }
    } catch (e) {
      // non-fatal: leave location as-is if something goes wrong
    }
  }

  function handleSeeAllTopics() {
    selectSeeAllTopics();
    setSearchInput("");
    setSearchTerm("");
    onTopicChange(null);
  }

  function handleQuestionTypeChange(nextQuestionType) {
    selectSurvey({ survey: nextQuestionType });
    setQuestionType(nextQuestionType);
  }

  function handleQuestionSortTypeChange(nextSortType) {
    selectSort({ sortType: nextSortType });
    setQuestionSortType(nextSortType);
  }

  function handleDashboardLocationChange(nextLocation, viz) {
    const locationId =
      nextLocation?.LocationId ||
      nextLocation?.LocationAbbr ||
      "ALL";
    selectLocation({ locationId });
    if (typeof onLocationChange === "function") {
      onLocationChange(nextLocation, viz);
    }
  }

  const trimmedTerms = searchTerm && searchTerm.trim().split(/\s+/);
  const topicMemo = {};
  // Helper: match question type text against selected audience filter
  function matchesQuestionType(q) {
    if (!questionType) return true; // no filter selected
    const val = (q.QuestionTypeText || "").toLowerCase();
    if (questionType === "Principal") return /principal/.test(val);
    if (questionType === "Teacher") return /teacher/.test(val); // matches lead health education teacher etc.
    if (questionType === "Both") {
      // Treat Both as union of Principal + Teacher OR explicit 'both'
      if (val === "both") return true;
      const hasPrincipal = /principal/.test(val);
      const hasTeacher = /teacher/.test(val);
      return hasPrincipal || hasTeacher; // union so user sees all related questions
    }
    return true;
  }

  // Helper: determine if a question should be shown for the selected location using the precomputed set
  function matchesLocation(q) {
    if (!location || location.LocationAbbr === "ALL") return true;
    // Do not hide tiles while computing availability; show and refine once ready
    if (locationFilterLoading) return true;
    if (!availableQuestionIds) return true;
    return availableQuestionIds.has(q.QuestionId);
  }

  const filteredQuestions = questions.filter((q) => {
    if (topicId && q.TopicId !== topicId) return false;
    if (subTopicId && q.SubTopicId !== subTopicId) return false;
  // Audience (question type) filter
    if (!matchesQuestionType(q)) return false;
  // Location filter (applies only when a specific, non-ALL location is chosen and question carries location metadata)
  if (!matchesLocation(q)) return false;

    const stringToSearch =
      q.ShortQuestionDescription +
      " " +
      q.LongQuestionDescription +
      " " +
      q.TopicDescription +
      " " +
      q.SubTopicDescription +
      " " +
      q.Keyword +
      " " +
      q.SubQuestions.map((q) => q.SubQuestion).join(" ");

    let include = 0;

    if (searchTerm && searchTerm.length > 0) {
      trimmedTerms &&
        trimmedTerms.forEach((term) => {
          const singular = pluralize.singular(term);
          const plural = pluralize.plural(term);
          const regex = new RegExp(singular + "|" + plural, "i");
          if (stringToSearch.match(regex)) {
            include++;
          }
        });

      if (trimmedTerms && include === trimmedTerms.length) {
        // Then every search term is present in some part of the stringToSearch
        topicMemo[q.TopicId] = true;
        return true;
      }
    } else {
      return true;
    }

    return false;
  });

  const sortedFilteredQuestions =
    questionSortType === "By Topic"
      ? filteredQuestions.slice().sort((a, b) => {
          const topicCompare = a.TopicId.localeCompare(b.TopicId);
          if (topicCompare !== 0) return topicCompare;
          // QuestionId from the API is set to the ShortNavId, so use QuestionId.
          const questionCompare = a.QuestionId.localeCompare(b.QuestionId);
          if (questionCompare !== 0) return questionCompare;
          return a.ShortQuestionDescription.localeCompare(b.ShortQuestionDescription);
        })
      : filteredQuestions
          .slice()
          .sort((a, b) => weightForQuestionId(a.QuestionId) - weightForQuestionId(b.QuestionId));

  // Debug: log tile count on initial load and whenever filters/state update the list
  useEffect(() => {
    if (questionId) return; // Only log on the dashboard (no question selected)
    const active = {
      topicId: topicId || null,
      subTopicId: subTopicId || null,
      searchTerm: (searchTerm && searchTerm.length > 0) ? searchTerm : null,
      questionType,
      questionSortType,
      locationAbbr: locationAbbr || "ALL",
      totalQuestions,
    };
    // eslint-disable-next-line no-console
    console.log(
      `[Dashboard] Tiles count: ${sortedFilteredQuestions.length}`,
      active
    );
  }, [
    // dependencies that affect the tiles list
    sortedFilteredQuestions,
    topicId,
    subTopicId,
    searchTerm,
    questionType,
    questionSortType,
    locationAbbr,
    questionId,
    totalQuestions,
  ]);

  //lets hide the dashboard
  if (questionId) return "";
  
  // Resolve subtopic id from topic + description when tile payload is missing subTopicId.
  function resolveSubTopicIdFromDescription(clickedTopicId, clickedSubTopicDescription) {
    if (!clickedTopicId || !clickedSubTopicDescription || !Array.isArray(topics)) return null;
    const topic = topics.find((t) => String(t.TopicId) === String(clickedTopicId));
    if (!topic || !Array.isArray(topic.SubTopics)) return null;
    const normalizedTarget = String(clickedSubTopicDescription).trim().toLowerCase();
    const matched = topic.SubTopics.find(
      (st) => String(st.SubTopicDescription || "").trim().toLowerCase() === normalizedTarget
    );
    return matched ? matched.SubTopicId : null;
  }

  // When clicking a subtopic inside a tile, filter the dashboard by that subtopic
  function handleSubtopicClickFromTile({
    topicId: clickedTopicId,
    subTopicId: clickedSubTopicId,
    subTopicDescription: clickedSubTopicDescription,
  }) {
    if (!clickedTopicId) return;
    const effectiveSubTopicId =
      clickedSubTopicId ||
      resolveSubTopicIdFromDescription(clickedTopicId, clickedSubTopicDescription);
    if (!effectiveSubTopicId) return;

    // Apply topic + subtopic together to avoid state/URL race conditions.
    if (typeof onSubTopicChange === "function") {
      onSubTopicChange({ topicId: clickedTopicId, subTopicId: effectiveSubTopicId });
    }
  }
  return (
    <div
      className="row justify-content-center "
      id="dashboard"
      style={{ fontSize: ".75em" }}
    >
      <nav
        className="col-sm-12 col-md-3 d-md-block sidebar-container "
        aria-label="Sidebar"
      >
         <SearchWidget
          value={searchInput}
          onChange={(v) => setSearchInput(v)}
          onDebouncedSearch={handleDebouncedSearch}
          onReset={handleSearchReset}
        />
        <div
          className="sidebar-sticky sidebar pl-2 pr-2 mt-3 mb-3"
          style={{
            border: "1px solid grey",
            borderRadius: "15px",
          }}
        >
          <Topics
            onSearchChange={handleDebouncedSearch}
            onTopicChange={onTopicChange}
            onSubTopicChange={onSubTopicChange}
            topicId={topicId}
            subTopicId={subTopicId}
            topics={topics}
            onSeeAllTopics={handleSeeAllTopics}
          />
        </div>
      </nav>

      <section className="col-sm align-items-center pr-lg-0 pr-md-0 pr-sm-0" aria-label="Dashboard Content">
        <div className="row d-block ml-0">
          <h1 className="tile-header">
            Explore School Health Profiles Questions
          </h1>
          <div className="tile-header2">
            Additional <b>locations</b> and <b>years</b> are available by selecting a question below.
          </div>
        </div>
        {/* create a new component for sort/filters that will update the dashboardTiles */}
        <DashboardFilters
          questionType={questionType}
          onQuestionTypeChange={handleQuestionTypeChange}
          questionSortType={questionSortType}
          onQuestionSortTypeChange={handleQuestionSortTypeChange}
          // location filter placeholders
          location={location}
          locations={locations}
          displayLocations={displayLocations}
          displayLocationDropdown={displayLocationDropdown}
          onLocationChange={handleDashboardLocationChange}
          setLocationSelectedFromDropdownMenu={setLocationSelectedFromDropdownMenu}
        />
        <hr />

  {locationFilterLoading && (
          <div className="alert alert-info" role="status" style={{ padding: "6px 10px", fontSize: "0.9em" }}>
            Filtering by location…
          </div>
        )}

        {questions && questions.length > 0 && !locationFilterLoading && (
          <DashboardTiles
            onVizTypeChange={onVizTypeChange}
            questions={sortedFilteredQuestions}
            searchTerm={searchTerm}
            topicId={topicId}
            onSubtopicClick={handleSubtopicClickFromTile}
            location={location}
          />
        )}
      </section>
    </div>
  );
};

export default DashboardView;
